import { useTranslation } from "react-i18next";
import { skipToken } from "@reduxjs/toolkit/query";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { UserDetailsContentProps } from "@/features/users/profile/types";
import {
  useGetCountryByIdQuery,
  useGetAchievementsQuery,
  useGetCommunityRolesQuery,
  useGetAreasOfExpertiseQuery,
} from "@/redux/service";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import UserAvatar from "@/domains/users/components/UserAvatar";
import BaseTag from "@/components_v2/Tags/BaseTag";
import EditIcon from "@/assets/icons/edit.svg?react";
import EmailIcon from "@/assets/icons/email.svg?react";
import EyeOffIcon from "@/assets/icons/eye-off.svg?react";
import PinIcon from "@/assets/icons/pin.svg?react";
import WorldIcon from "@/assets/icons/world.svg?react";
import LinkIcon from "@/assets/icons/link.svg?react";
import LinkedInIcon from "@/assets/icons/linkedin-logo.svg?react";
import OrganizationIcon from "@/assets/icons/organization-icon.svg?react";
import BaseDivider from "@/components_v2/Divider/BaseDivider";
import { Honorifics } from "@/shared/utils/constants";
import {
  AchievementResponse,
  CommunityRoleResponse,
} from "@/redux/common/types";
import { ExpertiseAreaResponse } from "@/redux/users/types";
import { isPaginatedResponse } from "@/redux/typeGuards";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import { useUserPermissions } from "@/features/users/hooks/useUserPermissions";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";

import "../styles.css";

const UserProfileDetailsDisplay = ({
  user,
  onEdit,
}: UserDetailsContentProps) => {
  const { t } = useTranslation();

  const currentUser = useSelector(selectCurrentUser);
  const achievementsResponse = useGetAchievementsQuery(
    { ids: user.profile.achievements, pagination: "off" },
    { skip: user.profile.achievements.length === 0 },
  );
  const communityRolesResponse = useGetCommunityRolesQuery(
    { ids: user.profile.communityRoles, pagination: "off" },
    { skip: user.profile.communityRoles.length === 0 },
  );
  const areasOfExpertiseResponse = useGetAreasOfExpertiseQuery(
    { ids: user.profile.areasOfExpertise, pagination: "off" },
    { skip: user.profile.areasOfExpertise.length === 0 },
  );
  const countryResponse = useGetCountryByIdQuery(
    user.profile.country ?? skipToken,
  );
  const { canEdit } = useUserPermissions(user, currentUser);

  const honorificLabel = Honorifics.getLabel(user.profile.honorific);

  return (
    <div className="user-details-content flex flex-col gap-400 full-height items-start">
      <div className="flex flex-col gap-200 items-center full-width">
        <UserAvatar
          user={user}
          shouldClickRedirect={false}
          className="user-avatar"
        />

        <h1 className="user-name header-m-medium full-width">
          {honorificLabel ? `${honorificLabel} ` : ""}
          {user.fullName || `${user.firstName} ${user.lastName}`}
        </h1>

        {(user.profile.jobTitle || user.profile.department) && (
          <div className="body-m-regular">
            {user.profile.jobTitle}
            {user.profile.jobTitle && user.profile.department && ` - `}
            {user.profile.department &&
              `${t("users.labels.department")}: ${user.profile.department}`}
          </div>
        )}

        <div className="flex flex-wrap gap-100 full-width justify-center">
          {achievementsResponse.isSuccess &&
          user.profile.achievements.length > 0 &&
          !isPaginatedResponse(achievementsResponse.data)
            ? achievementsResponse.data.map(
                (achievement: AchievementResponse) => (
                  <BaseTooltip key={achievement.id} title={achievement.label}>
                    <img src={achievement.icon} alt={achievement.label} />
                  </BaseTooltip>
                ),
              )
            : null}

          {communityRolesResponse.isSuccess &&
          user.profile.communityRoles.length > 0 &&
          !isPaginatedResponse(communityRolesResponse.data)
            ? communityRolesResponse.data.map(
                (communityRole: CommunityRoleResponse) => (
                  <BaseTooltip
                    key={communityRole.id}
                    title={communityRole.label}
                  >
                    <img src={communityRole.icon} alt={communityRole.label} />
                  </BaseTooltip>
                ),
              )
            : null}
        </div>

        {canEdit && (
          <BaseButton
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.TERTIARY}
            isFullWidth
            leadingIcon={
              <BaseSvgRenderer
                component={EditIcon}
                width="20"
                height="20"
                color="var(--neutral-900)"
              />
            }
            onClick={onEdit}
          >
            {t("users.labels.editProfile")}
          </BaseButton>
        )}
      </div>

      <div className="flex flex-col gap-250 full-width">
        <h3 className="section-title header-s-medium">
          {t("users.labels.personalInfo")}
        </h3>

        <div className="flex flex-col gap-200">
          {(user.email || (!user.email && currentUser?.id === user.id)) && (
            <div className="info-item flex gap-200 items-center body-m-regular">
              <BaseTooltip
                title={
                  user.isEmailHidden && currentUser?.id === user.id
                    ? t("users.labels.emailHiddenDescription")
                    : ""
                }
              >
                <BaseSvgRenderer
                  component={
                    user.isEmailHidden && currentUser?.id === user.id
                      ? EyeOffIcon
                      : EmailIcon
                  }
                  width="20"
                  height="20"
                  stroke="var(--neutral-700)"
                />
              </BaseTooltip>

              <BaseTooltip title={user.email || ""}>
                <span className="info-item-value info-item-ellipsis">
                  {user.email}
                </span>
              </BaseTooltip>
            </div>
          )}

          {user.organization?.name && (
            <div className="info-item flex gap-200 items-center body-m-regular">
              <BaseSvgRenderer
                component={OrganizationIcon}
                width="20"
                height="20"
                stroke="var(--neutral-700)"
              />
              <BaseTooltip title={user.organization.name}>
                <Link to={`/signatories/${user.organization.id}`}>
                  <span className="info-item-value info-item-ellipsis">
                    {user.organization.name}
                  </span>
                </Link>
              </BaseTooltip>
            </div>
          )}

          {user.profile.city && (
            <div className="info-item flex gap-200 items-center body-m-regular">
              <BaseSvgRenderer
                component={PinIcon}
                width="20"
                height="20"
                stroke="var(--neutral-700)"
              />
              <span className="info-item-value info-item-ellipsis">
                {user.profile.city},{" "}
                {countryResponse.isSuccess ? (
                  countryResponse.data.name
                ) : (
                  <BaseSpinner size="small" />
                )}
              </span>
            </div>
          )}

          {countryResponse.isFetching && (
            <div className="info-item flex gap-200 items-center body-m-regular">
              <BaseSvgRenderer
                component={WorldIcon}
                width="20"
                height="20"
                stroke="var(--neutral-700)"
              />
              <span className="info-item-value">
                {countryResponse.isSuccess ? (
                  countryResponse.data.name
                ) : (
                  <BaseSpinner size="small" />
                )}
              </span>
            </div>
          )}
        </div>
      </div>

      {user.profile.linkedIn || user.profile.website ? (
        <div className="flex flex-col gap-400 full-width">
          <BaseDivider />

          <div className="flex flex-col gap-250">
            <h3 className="section-title header-s-medium">
              {t("users.labels.webAndSocial")}
            </h3>
            <div className="flex flex-col gap-200">
              {user.profile.linkedIn && (
                <div className="info-item flex gap-200 items-center body-m-regular">
                  <BaseSvgRenderer
                    component={LinkedInIcon}
                    width="20"
                    height="20"
                    stroke="var(--neutral-700)"
                  />
                  <BaseTooltip title={user.profile.linkedIn as string}>
                    <a
                      href={user.profile.linkedIn as string}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="info-item-ellipsis"
                    >
                      {user.profile.linkedIn}
                    </a>
                  </BaseTooltip>
                </div>
              )}

              {user.profile.website && (
                <div className="info-item flex gap-200 items-center body-m-regular">
                  <BaseSvgRenderer
                    component={LinkIcon}
                    width="20"
                    height="20"
                    stroke="var(--neutral-700)"
                  />
                  <BaseTooltip title={user.profile.website as string}>
                    <a
                      href={user.profile.website as string}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="info-item-ellipsis"
                    >
                      {user.profile.website}
                    </a>
                  </BaseTooltip>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {user.profile.areasOfExpertise.length > 0 && (
        <div className="flex flex-col gap-400 full-width">
          <BaseDivider />
          <div className="flex flex-col gap-250">
            <h3 className="section-title header-s-medium">
              {t("users.labels.areasOfExpertise")}
            </h3>
            <div className="flex flex-wrap gap-100">
              {areasOfExpertiseResponse.isSuccess &&
              !isPaginatedResponse(areasOfExpertiseResponse.data)
                ? areasOfExpertiseResponse.data.map(
                    (areaOfExpertise: ExpertiseAreaResponse) => (
                      <BaseTag
                        key={areaOfExpertise.id}
                        label={areaOfExpertise.name}
                      />
                    ),
                  )
                : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDetailsDisplay;
